import {
    ArrowRightOutlined,
    BarChartOutlined,
    CheckCircleOutlined,
    MailOutlined,
    SafetyCertificateOutlined,
    TeamOutlined,
    UserOutlined,
} from '@ant-design/icons'
import { Alert, Button, Card, Form, Input, Result } from 'antd'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import BrandLogo from 'assets/img/workplansiq-logo.svg'
import useAuthRepo from 'services/repo/useAuthRepo'
import Constant from 'utils/Constants'

const errorMessageFor = (response) => {
    if (response === false) return 'We could not create your account. Please check your details and try again.'

    const detail = response?.detail || response?.message
    if (typeof detail === 'string' && detail.trim()) return detail

    return 'Registration is temporarily unavailable. Please try again later.'
}

export default function Register() {
    const { register, startGoogleOidc } = useAuthRepo(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [registeredEmail, setRegisteredEmail] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoadingGoogle, setIsLoadingGoogle] = useState(false)

    const handleRegister = async ({ email, fullName }) => {
        setErrorMessage('')
        setIsSubmitting(true)

        const response = await register({ email: email.trim(), fullName: fullName.trim() })
        setIsSubmitting(false)

        if (response && response !== false && response?.isError !== true) {
            setRegisteredEmail(email.trim())
            return
        }

        setErrorMessage(errorMessageFor(response))
    }

    const handleGoogleLogin = async () => {
        setErrorMessage('')
        setIsLoadingGoogle(true)
        const response = await startGoogleOidc()
        const authorizationUrl = response?.authorizationUrl || response?.data?.authorizationUrl

        if (authorizationUrl) {
            window.location.assign(authorizationUrl)
            return
        }

        setErrorMessage(response?.detail || 'Unable to start Google sign-in. Please try again.')
        setIsLoadingGoogle(false)
    }

    return (
        <div className="login-page register-page">
            <div className="wrap-login">
                <div className="login-shell">
                    <section className="login-brand-panel">
                        <img src={BrandLogo} alt="WorkPlansIQ" className="login-brand-logo" />
                        <div className="login-brand-copy">
                            <span className="login-eyebrow">EVIDENCE-BASED WORKFORCE PLANNING</span>
                            <h1>Plan your workforce with confidence.</h1>
                            <p>Turn workload evidence into clear headcount decisions that support your organisation's strategic priorities.</p>
                        </div>
                        <div className="login-benefits">
                            <div><BarChartOutlined /><span><strong>Evidence-based workload analysis</strong>Understand capacity using measurable workload data.</span></div>
                            <div><TeamOutlined /><span><strong>Precise headcount recommendations</strong>Translate operational demand into defensible staffing needs.</span></div>
                            <div><SafetyCertificateOutlined /><span><strong>Strategy-aligned workforce planning</strong>Connect workforce decisions directly to organisational goals.</span></div>
                        </div>
                    </section>

                    <section className="login-form-panel register-form-panel">
                        <Card className={`card${errorMessage ? ' login-has-error' : ''}`}>
                            {registeredEmail ? (
                                <Result
                                    status="success"
                                    icon={<CheckCircleOutlined />}
                                    title="Account created"
                                    subTitle={`Your WorkPlansIQ account for ${registeredEmail} is ready.`}
                                    extra={<Button type="primary" size="large"><Link to="/">Continue to sign in</Link></Button>}
                                />
                            ) : (
                                <>
                                    <a className="login-back-home register-back-home" href={Constant.commercialWebURL}>
                                        ← Back to website
                                    </a>
                                    <span className="auth-container-login-eyebrow">FREE ACCOUNT SETUP</span>
                                    <h1 className="auth-container-login-title">Sign Up</h1>
                                    <p className="auth-container-login-subtitle">Start planning your workforce with better data.</p>

                                    {errorMessage ? <Alert className="login-error" message={errorMessage} type="error" showIcon /> : null}

                                    <Form layout="vertical" onFinish={handleRegister} autoComplete="on">
                                        <Button
                                            block
                                            className="login-google"
                                            size="large"
                                            loading={isLoadingGoogle}
                                            disabled={isSubmitting}
                                            onClick={handleGoogleLogin}
                                        >
                                            {!isLoadingGoogle ? <span className="google-mark" aria-hidden="true">G</span> : null}
                                            Continue with Google
                                        </Button>

                                        <div className="login-divider"><span>or</span></div>

                                        <Form.Item label="Work Email" name="email" rules={[
                                            { required: true, message: 'Please enter your work email.' },
                                            { type: 'email', message: 'Please enter a valid email address.' },
                                        ]}>
                                            <Input prefix={<MailOutlined />} type="email" autoComplete="email" placeholder="your.name@company.com" maxLength={255} onChange={() => setErrorMessage('')} />
                                        </Form.Item>

                                        <Form.Item label="Full Name" name="fullName" rules={[
                                            { required: true, message: 'Please enter your full name.' },
                                            { min: 2, message: 'Full name must contain at least 2 characters.' },
                                        ]}>
                                            <Input prefix={<UserOutlined />} autoComplete="name" placeholder="e.g. Lukman Hakim" maxLength={255} onChange={() => setErrorMessage('')} />
                                        </Form.Item>

                                        <Button block className="login-submit" type="primary" htmlType="submit" loading={isSubmitting} size="large" disabled={isLoadingGoogle}>
                                            Sign Up {!isSubmitting ? <ArrowRightOutlined /> : null}
                                        </Button>
                                    </Form>

                                    <p className="register-agreement">
                                        By signing up, you agree to our Terms of Service and Privacy Policy.
                                    </p>
                                    <p className="login-register-link">Already have an account? <Link to="/">Sign in here</Link></p>
                                </>
                            )}
                        </Card>
                    </section>
                </div>
            </div>
        </div>
    )
}
